import type { deps } from "../api";
import * as dom from "../dom";
import type { FormFieldConstructor, FormFieldInterface } from "../dom/form";
import { invoke as invokeHandler } from "../invoke";
import type { APIDependencies, FieldEntry, FieldOptionsEntry, HCInstance, HCVersion } from "./types";

export type ApiMap = Partial<{ [K in APIDependencies]: (typeof deps)[K] }>;

function toFieldIdentifier(id: string | number): string {
  return typeof id === "number" || !isNaN(Number(id))
    ? `custom_field_${id}`
    : String(id);
}

export abstract class HCBase implements HCInstance {
  protected api: ApiMap;
  readonly dom = dom;
  private fieldCache = new Map<string, FormFieldInterface>();

  constructor(
    private readonly version: HCVersion,
    apiDeps: ApiMap | null,
  ) {
    this.api = apiDeps ?? {};
  }

  abstract readonly FormField: FormFieldConstructor;

  protected abstract createField(identifier: string): FormFieldInterface;

  private resolveField(identifier: string): FormFieldInterface {
    if (!this.fieldCache.has(identifier)) {
      this.fieldCache.set(identifier, this.createField(identifier));
    }
    return this.fieldCache.get(identifier)!;
  }

  protected use<K extends APIDependencies>(dep: K): (typeof deps)[K] {
    const module = this.api[dep];
    if (!module) {
      throw new Error(
        `"${dep}" was not provided as a dependency. Pass it when creating the instance: create("${this.version}", ["${dep}"])`,
      );
    }
    return module as (typeof deps)[K];
  }

  invoke(path: string, ...args: unknown[]): void {
    invokeHandler(path, (id) => this.resolveField(id), ...args);
  }

  hideFields(fields: FieldEntry[]): void {
    fields.forEach(({ id }) => {
      this.invoke(`ticketFields:${toFieldIdentifier(id)}.hide`);
    });
  }

  showFields(fields: FieldEntry[]): void {
    fields.forEach(({ id }) => {
      this.invoke(`ticketFields:${toFieldIdentifier(id)}.show`);
    });
  }

  hideFieldOptions(fields: FieldOptionsEntry[]): void {
    fields.forEach(({ id, options }) => {
      this.invoke(`ticketFields:${toFieldIdentifier(id)}.hideOptions`, options);
    });
  }

  showFieldOptions(fields: FieldOptionsEntry[]): void {
    fields.forEach(({ id, options }) => {
      this.invoke(`ticketFields:${toFieldIdentifier(id)}.showOptions`, options);
    });
  }

  resetFieldOptions(fields: FieldEntry[]): void {
    fields.forEach(({ id }) => {
      this.invoke(`ticketFields:${toFieldIdentifier(id)}.resetOptions`);
    });
  }

  filterFieldOptions(fields: FieldOptionsEntry[]): void {
    fields.forEach(({ id, options }) => {
      this.invoke(
        `ticketFields:${toFieldIdentifier(id)}.filterOptions`,
        options,
      );
    });
  }

  clearFieldFilter(fields: FieldEntry[]): void {
    fields.forEach(({ id }) => {
      this.invoke(`ticketFields:${toFieldIdentifier(id)}.clearFilter`);
    });
  }

  abstract getAllArticles(): Promise<any>;
}
